import {
  Anchor,
  Button,
  Card,
  CardContent,
  H5,
  InputCheckbox,
} from "@sellar-io/components";

import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import { Icon } from "../../../components";
import { useDashboardContext } from "../../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../components/pageContainer/PageContainer";
import { BUSINESS } from "../../../constants/routes";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";

import dayjs from "dayjs";
import { useBusinessAcceptTerms } from "../../../hooks/api/v2/business/useBusinessAcceptTerms";

export const TermsAndConditions = (): ReactElement | null => {
  const navigate = useNavigate();
  const { isTwoPanel } = useDashboardContext();
  const [accepted, setAccepted] = useState(false);

  const { business: userBusiness } = useSellarCurrentBusiness();

  const businessId = userBusiness?.id;

  if (!businessId) throw new Error("Cannot find business");

  const businessAcceptTermsMutation = useBusinessAcceptTerms(businessId);
  const termsAndConditions = userBusiness.TermsAndConditions;
  const termsAccepted = !!termsAndConditions?.acceptedAt;
  const acceptedDate = termsAccepted
    ? dayjs(userBusiness.TermsAndConditions?.acceptedAt).format("DD/MM/YYYY")
    : "";

  return (
    <>
      <NavigationBar
        title={"Terms & conditions"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(BUSINESS)}
      />
      <div className="mx-auto p-2">
        <PageContainer className={"mx-auto"}>
          <div>
            <Card className="bg-white">
              <CardContent className={"flex flex-col gap-4 p-4"}>
                <H5 className="m-0">Terms & conditions</H5>
                {termsAccepted ? (
                  <>
                    <p>
                      These terms & conditions were agreed to on behalf of{" "}
                      {userBusiness.name} on {acceptedDate} by{" "}
                      {termsAndConditions.BusinessUser.User.name}.
                    </p>
                    <Anchor
                      href="https://sellar.io/terms"
                      target="_blank"
                      className="mt-4 flex items-center font-bold uppercase no-underline"
                    >
                      <Icon icon="OPEN_IN_NEW" className="mr-1" /> View terms &
                      conditions
                    </Anchor>
                  </>
                ) : (
                  <TermsAndConditionsCopy supplierName={userBusiness.name} />
                )}

                {!termsAccepted && (
                  <>
                    <div className="mt-4 flex items-center gap-2">
                      <InputCheckbox
                        className="flex-none"
                        onChange={(e) => setAccepted(e.target.checked)}
                      />
                      I agree to the terms and conditions and am legally
                      authorised to do so on behalf of {userBusiness.name}.
                    </div>
                    <Button
                      className="flex-none"
                      onClick={() => {
                        businessAcceptTermsMutation.mutate(undefined, {
                          onError: () => setAccepted(false),
                        });
                      }}
                      disabled={!accepted}
                      loading={businessAcceptTermsMutation.isPending}
                    >
                      Confirm agreement
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </PageContainer>
      </div>
    </>
  );
};

export const TermsAndConditionsCopy = ({
  supplierName,
}: {
  supplierName: string;
}) => {
  const enforcementDate = dayjs("2024-12-01").format("DD/MM/YYYY");

  return (
    <div className="flex flex-col gap-4">
      <p>
        From, {enforcementDate}, Sellar is introducing new Terms and Conditions
        to govern {supplierName}'s use of Sellar. Sellar's new Terms and
        Conditions can be seen here:
      </p>

      <Anchor
        href="https://sellar.io/terms"
        target="_blank"
        className="flex items-center font-bold uppercase no-underline"
      >
        <Icon icon="OPEN_IN_NEW" className="mr-1" /> View terms & conditions
      </Anchor>

      <p>
        To continue using Sellar you must agree to Sellar's new Terms and
        Conditions on behalf of {supplierName}.
      </p>

      <p>
        For more information on Sellar's new Terms and Conditions please see the
        email sent to {supplierName}'s Super Admin.
      </p>
    </div>
  );
};
